import { useEventSource } from '@revenuemarket/http';
import { useQueryClient } from '@tanstack/react-query';
import { getCookie, deleteCookie } from 'cookies-next';
import { debounce } from 'lodash-es';
import { deleteSuperToken } from '~apis/auth/signin';
import { API_URL } from '~constants/env';
import { BankAccountsQueryKey } from '~hooks/queries/bank-accounts/query';
import { IntegratedScrapeQueryStatusKeys } from '~hooks/queries/scraping/useGetIntegratedScrapeStatus';
import { TransactionsQueryKey } from '~hooks/queries/transactions/query';

import { CashFlowQueryKey } from './queries/cash-flow/query';
import { DashboardQueryKey } from './queries/dashboard/query';
import { OrganizationsQueryKey } from './queries/organizations/query';
import { SettingsQueryKey } from './queries/setting/query';
import { useCompanyId } from './useCompanyId';

const useGlobalEventSource = () => {
  const queryClient = useQueryClient();
  const { companyId } = useCompanyId();

  const token = getCookie('access_super_token') || getCookie('accessToken');

  const debouncedInvalidateQueries = debounce((companyId: string) => {
    queryClient.invalidateQueries(IntegratedScrapeQueryStatusKeys.scrapeStatus(companyId));
    queryClient.invalidateQueries(OrganizationsQueryKey.info({ companyId }));
    queryClient.invalidateQueries([...SettingsQueryKey.default()]);
    queryClient.invalidateQueries(BankAccountsQueryKey.list({ companyId }));
    queryClient.invalidateQueries(TransactionsQueryKey.list({ companyId }));
    queryClient.invalidateQueries(
      DashboardQueryKey.list({
        companyId,
      }),
    );
    queryClient.invalidateQueries(['approval', companyId]);

    queryClient.invalidateQueries(
      DashboardQueryKey.scheduledInvoiceMapping({
        companyId,
        type: 'PURCHASE',
      }),
    );

    queryClient.invalidateQueries(
      DashboardQueryKey.scheduledInvoiceMapping({
        companyId,
        type: 'SALES',
      }),
    );

    queryClient.invalidateQueries([CashFlowQueryKey.trends(), companyId]);
  }, 300);

  useEventSource(
    `${API_URL!}/api/v2/companies/${companyId}/sse/subscribe?type=INTEGRATED_SCRAPE`,
    () => {
      debouncedInvalidateQueries(companyId);
    },
    Boolean(companyId) && Boolean(token),
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      withCredentials: false,
    },
    () => {
      deleteCookie('accessToken');
      deleteSuperToken();
    },
  );
};

export default useGlobalEventSource;
